/**
 * Proof lifecycle, §3.4.
 *
 * One state machine, shared by the API, the chain worker and the UI, because
 * three implementations of "is this confirmed yet" is how a product ends up
 * showing a confirmed badge for a transaction that never landed.
 *
 * The rule that shapes everything here: the owner may receive the output before
 * chain finality, but the UI must label it Proof pending until the transaction
 * is confirmed and indexed. There is deliberately no transition that reaches
 * CONFIRMED_AND_INDEXED from client-side optimism — the only way in is an
 * observed receipt at depth plus an indexer acknowledgement.
 */

/** @typedef {typeof ProofState[keyof typeof ProofState]} ProofStateValue */

export const ProofState = Object.freeze({
  QUEUED: 'Queued',
  EXECUTING: 'Executing',
  EVIDENCE_FINALIZING: 'Evidence finalizing',
  ATTESTATION_SIGNING: 'Attestation signing',
  TRANSACTION_SUBMITTED: 'Transaction submitted',
  CONFIRMING: 'Confirming',
  CONFIRMED_AND_INDEXED: 'Confirmed and indexed',
  // Recoverable, per §3.4. Not terminal: an authorized retry returns to
  // ATTESTATION_SIGNING with the same runIdHash.
  PROOF_FAILED: 'Proof failed',
});

/** The ordered happy path, for progress indicators. */
export const PROOF_SEQUENCE = Object.freeze([
  ProofState.QUEUED,
  ProofState.EXECUTING,
  ProofState.EVIDENCE_FINALIZING,
  ProofState.ATTESTATION_SIGNING,
  ProofState.TRANSACTION_SUBMITTED,
  ProofState.CONFIRMING,
  ProofState.CONFIRMED_AND_INDEXED,
]);

/**
 * Only CONFIRMED_AND_INDEXED is proven. Everything else, including a state
 * where the owner already has the deliverable in hand, is Proof pending.
 * §3.4 forbids showing a confirmed state on optimism alone.
 *
 * @param {ProofStateValue} state
 */
export function isProven(state) {
  return state === ProofState.CONFIRMED_AND_INDEXED;
}

/** @param {ProofStateValue} state */
export function isPending(state) {
  return state !== ProofState.CONFIRMED_AND_INDEXED && state !== ProofState.PROOF_FAILED;
}

/**
 * The label the UI must show. Exported so no component invents its own copy
 * and accidentally implies proof where there is none.
 *
 * @param {ProofStateValue} state
 */
export function proofBadge(state) {
  if (isProven(state)) return { label: 'Proof complete', tone: 'success' };
  if (state === ProofState.PROOF_FAILED) return { label: 'Proof failed', tone: 'danger' };
  return { label: 'Proof pending', tone: 'pending' };
}

/**
 * Whether the deliverable itself can be shown. The output is available well
 * before the proof is, and hiding it until finality would make the product feel
 * broken for no safety benefit — the labelling is what carries the honesty.
 *
 * @param {ProofStateValue} state
 */
export function outputAvailable(state) {
  const i = PROOF_SEQUENCE.indexOf(state);
  return state === ProofState.PROOF_FAILED || (i >= PROOF_SEQUENCE.indexOf(ProofState.EVIDENCE_FINALIZING));
}

const TRANSITIONS = Object.freeze({
  [ProofState.QUEUED]: [ProofState.EXECUTING, ProofState.PROOF_FAILED],
  [ProofState.EXECUTING]: [ProofState.EVIDENCE_FINALIZING, ProofState.PROOF_FAILED],
  [ProofState.EVIDENCE_FINALIZING]: [ProofState.ATTESTATION_SIGNING, ProofState.PROOF_FAILED],
  [ProofState.ATTESTATION_SIGNING]: [ProofState.TRANSACTION_SUBMITTED, ProofState.PROOF_FAILED],
  [ProofState.TRANSACTION_SUBMITTED]: [ProofState.CONFIRMING, ProofState.PROOF_FAILED],
  // Note what is absent: CONFIRMING cannot reach CONFIRMED_AND_INDEXED without
  // advance() being given both a receipt at depth and an indexer acknowledgement.
  [ProofState.CONFIRMING]: [ProofState.CONFIRMED_AND_INDEXED, ProofState.PROOF_FAILED],
  [ProofState.CONFIRMED_AND_INDEXED]: [],
  // Retry re-enters at signing. It never re-executes the run: §3.4 requires the
  // same logical run and the same runIdHash to be retained.
  [ProofState.PROOF_FAILED]: [ProofState.ATTESTATION_SIGNING],
});

export class InvalidProofTransition extends Error {
  constructor(from, to) {
    super(`cannot move a proof from "${from}" to "${to}"`);
    this.name = 'InvalidProofTransition';
  }
}

/**
 * @typedef {object} ProofSnapshot
 * @property {string} runId
 * @property {string} runIdHash
 * @property {ProofStateValue} state
 * @property {string | null} txHash
 * @property {number | null} receiptBlock
 * @property {number} confirmations
 * @property {boolean} indexed
 * @property {number} retries
 * @property {string | null} lastError
 */

/**
 * @param {{runId: string, runIdHash: string}} run
 * @returns {ProofSnapshot}
 */
export function createProof(run) {
  return {
    runId: run.runId,
    runIdHash: run.runIdHash,
    state: ProofState.QUEUED,
    txHash: null,
    receiptBlock: null,
    confirmations: 0,
    indexed: false,
    retries: 0,
    lastError: null,
  };
}

/**
 * Advance a proof. Pure: returns a new snapshot rather than mutating, so the
 * same function can drive an optimistic UI reducer and a durable worker without
 * one of them quietly diverging.
 *
 * @param {ProofSnapshot} proof
 * @param {ProofStateValue} next
 * @param {object} [evidence]
 * @param {string} [evidence.txHash]
 * @param {number} [evidence.receiptBlock]
 * @param {number} [evidence.confirmations]
 * @param {number} [evidence.requiredConfirmations]
 * @param {boolean} [evidence.indexed]
 * @param {string} [evidence.error]
 * @returns {ProofSnapshot}
 */
export function advance(proof, next, evidence = {}) {
  const allowed = TRANSITIONS[proof.state];
  if (!allowed || !allowed.includes(next)) throw new InvalidProofTransition(proof.state, next);

  if (next === ProofState.TRANSACTION_SUBMITTED && !evidence.txHash) {
    throw new Error('a submitted proof must carry the transaction hash it was submitted as');
  }

  if (next === ProofState.CONFIRMED_AND_INDEXED) {
    // The whole point of the module. Both facts are required, and neither can
    // be inferred by the client.
    const required = evidence.requiredConfirmations ?? 3;
    const depth = evidence.confirmations ?? proof.confirmations;
    if (depth < required) {
      throw new Error(`refusing to mark proof complete at ${depth} confirmations; ${required} required`);
    }
    if (evidence.indexed !== true) {
      throw new Error('refusing to mark proof complete before the indexer has acknowledged the block');
    }
  }

  const retrying = proof.state === ProofState.PROOF_FAILED && next === ProofState.ATTESTATION_SIGNING;

  return {
    ...proof,
    state: next,
    txHash: evidence.txHash ?? proof.txHash,
    receiptBlock: evidence.receiptBlock ?? proof.receiptBlock,
    confirmations: evidence.confirmations ?? proof.confirmations,
    indexed: evidence.indexed ?? proof.indexed,
    retries: retrying ? proof.retries + 1 : proof.retries,
    lastError: next === ProofState.PROOF_FAILED ? (evidence.error ?? 'unknown error') : proof.lastError,
  };
}

/**
 * Map a chain-worker receipt state onto a proof state.
 *
 * @param {string} receiptState a ReceiptState value from @cortis/chain-worker
 * @returns {ProofStateValue}
 */
export function fromReceiptState(receiptState) {
  switch (receiptState) {
    case 'SUBMITTED':
    case 'SEEN_IN_MEMPOOL_OR_BUNDLER':
      return ProofState.CONFIRMING;
    case 'INCLUDED':
    case 'FINALIZED':
      // Included and even finalized are still Confirming from the product's
      // point of view: §3.4 requires the indexer to have caught up before the
      // proof is complete, because that is where the UI reads history from.
      return ProofState.CONFIRMING;
    case 'INDEXED':
      return ProofState.CONFIRMED_AND_INDEXED;
    case 'REVERTED':
    case 'EXPIRED':
    case 'DROPPED_REPLACED':
    case 'CANCELLED':
      return ProofState.PROOF_FAILED;
    default:
      return ProofState.CONFIRMING;
  }
}
